import { useCallback } from "react";

import { UnitSystem } from "types/units";

import { useEvaUnitCore } from "./useEvaUnitCore";
import useUserUnit from "./useUserUnit";

const UNIT_LABEL_MAP: Record<string, Record<number, string>> = {
  // Volume
  m3: {
    [UnitSystem.SI]: "m³",
    [UnitSystem.Hybrid]: "m³",
    [UnitSystem.Imperial]: "ft³"
  },
  // Flow rate
  "m3/min": {
    [UnitSystem.SI]: "m³/min",
    [UnitSystem.Hybrid]: "m³/min",
    [UnitSystem.Imperial]: "ft³/min"
  },
  // Mass
  t: {
    [UnitSystem.SI]: "t",
    [UnitSystem.Hybrid]: "t",
    [UnitSystem.Imperial]: "ton"
  },
  // Pressure
  kPa: {
    [UnitSystem.SI]: "kPa",
    [UnitSystem.Hybrid]: "kPa",
    [UnitSystem.Imperial]: "psi"
  },
  // Density
  "kg/m3": {
    [UnitSystem.SI]: "kg/m³",
    [UnitSystem.Hybrid]: "kg/m³",
    [UnitSystem.Imperial]: "lb/ft³"
  }
};

export default function useUnitLabel() {
  const { getTargetUnit, isReady } = useEvaUnitCore();
  const unitType = useUserUnit();

  const getLabel = useCallback(
    (siUnit: string): string => {
      if (UNIT_LABEL_MAP[siUnit]?.[unitType]) {
        return UNIT_LABEL_MAP[siUnit][unitType];
      }

      // Fallback to the unit itself if no mapping exists
      return siUnit;
    },
    [unitType]
  );

  const getTargetUnitId = useCallback(
    (siUnit: string): string => {
      if (!isReady) return siUnit;
      return getTargetUnit(siUnit, unitType);
    },
    [isReady, unitType, getTargetUnit]
  );

  return {
    getLabel,
    getTargetUnitId,
    isReady,
    unitType
  };
}
